import { Box, Button, Card, CardContent, Chip, Divider, Switch, Typography } from '@mui/material';
import { formatDate } from 'date-fns';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '../../hooks/useBoolean';
import { CreateFormAccessBody, FormAccessWithUser } from '../../services/apiSLTP/qhse/forms/formAccess';
import { FormTemplateWithSections, UpdateFormTemplateBody } from '../../services/apiSLTP/qhse/forms/formTemplates';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import ScrollPageContainer from '../layout/ScrollPageContainer';
import CustomTextInput from '../ui/CustomTextInput';
import Icon from '../ui/Icon';
import AddFormAccessModal from './AddUserFormAccessModal';

interface FormSettingsProps {
    formTemplate: FormTemplateWithSections;
    onUpdateTemplate: (templateId: string, data: UpdateFormTemplateBody, file?: File) => void;
    disabled?: boolean;
    formAccesses?: FormAccessWithUser[] | null;
    onCreateFormAccess: (data: CreateFormAccessBody) => void;
    onDeleteFormAccess: (formAccessId: string) => void;
}

const FormSettings: React.FC<FormSettingsProps> = ({
    formTemplate,
    onUpdateTemplate,
    disabled,
    formAccesses,
    onCreateFormAccess,
    onDeleteFormAccess,
}) => {
    const { t } = useTranslation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const addAccessModal = useBoolean();

    const [formColor, setFormColor] = useState<string>(formTemplate?.type?.color || '#1976d2');
    const [hasMaxAttendees, setHasMaxAttendees] = useState<boolean>(formTemplate?.hasMaxAttendees || false);
    const [maxAttendees, setMaxAttendees] = useState<string>(formTemplate?.maxAttendees.toString() || '');
    const [allowSignature, setAllowSignature] = useState<boolean>(formTemplate?.allowSignature || false);
    const [allowPhotos, setAllowPhotos] = useState<boolean>(formTemplate?.allowPhotos || false);
    const [singleResponse, setSingleResponse] = useState<boolean>(formTemplate?.allowSingleResponsePerUser || false);
    const [isPublic, setIsPublic] = useState<boolean>(formTemplate?.isPublic || false);
    const [isSiteEnabled, setIsSiteEnabled] = useState<boolean>(formTemplate?.isSiteEnabled || false);

    const renderSettingRow = (label: string, description: string | null, control: React.ReactNode) => (
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ gap: 2 }}>
            <Box flex={1} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body1" fontWeight="bold">
                    {label}
                </Typography>
                {description && (
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                )}
            </Box>
            <Box>{control}</Box>
        </Box>
    );

    return (
        <ScrollPageContainer ref={scrollContainerRef}>
            <Card sx={{ borderRadius: 0 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {capitalizeFirstLetter(t('formSettings'))}
                    </Typography>
                    <Divider />

                    <Box sx={{ paddingLeft: 5, display: 'flex', flexDirection: 'column', gap: 3, py: 3 }}>
                        {renderSettingRow(
                            capitalizeFirstLetter(t('version')),
                            t('versionDescription', {
                                date: formatDate(new Date(formTemplate.createdAt), 'dd/MM/yyyy'),
                                user: formatName(formTemplate.createdBy.firstName, formTemplate.createdBy.lastName),
                            }),
                            <Typography variant="body1" sx={{ paddingRight: 2 }}>
                                {formTemplate.version}
                            </Typography>
                        )}
                        <Divider />
                        {renderSettingRow(
                            capitalizeFirstLetter(t('maxAttendees')),
                            t('maxAttendeesDescription'),
                            <Switch
                                checked={hasMaxAttendees}
                                onChange={(e) => {
                                    setHasMaxAttendees(e.target.checked);
                                    onUpdateTemplate(formTemplate._id, { hasMaxAttendees: e.target.checked });
                                }}
                                disabled={disabled}
                                color="info"
                            />
                        )}
                        {hasMaxAttendees && (
                            <CustomTextInput
                                type="number"
                                value={maxAttendees}
                                onChange={(text) => {
                                    const val = Number(text);
                                    setMaxAttendees(text);
                                    if (text.length === 0 || isNaN(val) || val < 0) return;
                                    onUpdateTemplate(formTemplate._id, { maxAttendees: val });
                                }}
                                disabled={disabled}
                            />
                        )}
                        <Divider />
                        {renderSettingRow(
                            capitalizeFirstLetter(t('allowSignature')),
                            t('allowSignatureDescription'),
                            <Switch
                                checked={allowSignature}
                                onChange={(e) => {
                                    setAllowSignature(e.target.checked);
                                    onUpdateTemplate(formTemplate._id, { allowSignature: e.target.checked });
                                }}
                                disabled={disabled}
                                color="info"
                            />
                        )}
                        <Divider />
                        {renderSettingRow(
                            capitalizeFirstLetter(t('allowPhotos')),
                            t('allowPhotosDescription'),
                            <Switch
                                checked={allowPhotos}
                                onChange={(e) => {
                                    setAllowPhotos(e.target.checked);
                                    onUpdateTemplate(formTemplate._id, { allowPhotos: e.target.checked });
                                }}
                                disabled={disabled}
                                color="info"
                            />
                        )}
                        <Divider />
                        {renderSettingRow(
                            capitalizeFirstLetter(t('isSiteEnabled')),
                            t('isSiteEnabledDescription'),
                            <Switch
                                checked={isSiteEnabled}
                                onChange={(e) => {
                                    setIsSiteEnabled(e.target.checked);
                                    onUpdateTemplate(formTemplate._id, { isSiteEnabled: e.target.checked });
                                }}
                                disabled={disabled}
                                color="info"
                            />
                        )}
                        <Divider />
                        {renderSettingRow(
                            capitalizeFirstLetter(t('singleResponseByUser')),
                            t('singleResponseDescription'),
                            <Switch
                                checked={singleResponse}
                                onChange={(e) => {
                                    setSingleResponse(e.target.checked);
                                    onUpdateTemplate(formTemplate._id, { allowSingleResponsePerUser: e.target.checked });
                                }}
                                disabled={disabled}
                                color="info"
                            />
                        )}
                        <Divider />
                        {renderSettingRow(
                            capitalizeFirstLetter(t('everybodyCanRespond')),
                            t('everybodyCanRespondDescription'),
                            <Switch
                                checked={isPublic}
                                onChange={(e) => {
                                    setIsPublic(e.target.checked);
                                    onUpdateTemplate(formTemplate._id, { isPublic: e.target.checked });
                                }}
                                color="info"
                            />
                        )}
                        {!isPublic && (
                            <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
                                <Button
                                    onClick={() => {
                                        addAccessModal.setTrue();
                                    }}
                                    startIcon={<Icon name="addCircle" />}
                                >
                                    {capitalizeFirstLetter(t('addUser'))}
                                </Button>
                                {formAccesses?.map((fa) => (
                                    <Chip
                                        key={fa._id}
                                        label={formatName(fa.user?.firstName, fa.user?.lastName)}
                                        onDelete={() => onDeleteFormAccess(fa._id)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
            {addAccessModal.value && (
                <AddFormAccessModal
                    open={addAccessModal.value}
                    handleClose={addAccessModal.setFalse}
                    onCreateFormAccess={onCreateFormAccess}
                    groupId={formTemplate.groupId}
                />
            )}
        </ScrollPageContainer>
    );
};

export default FormSettings;
